import { useEnabledCompetences } from "@/api/Shifts";
import { createTag, deleteTag, tagsByGroupQueryOptions } from "@/api/Templates";
import { deducedError } from "@/Utils/ErrorUtils";
import { useLingui } from "@lingui/react/macro";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useFormContext } from "react-hook-form";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import Checkbox from "@/components/Checkbox/Checkbox";
import {
  medicalCompetenceDictionary,
  medicalCompetenceSchema,
} from "@medoma/models/shifts";
import { tagGroupsSchema } from "@medoma/models/templates";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  computeCompetenceTagSync,
  parseCompetenceTagName,
  sortCompetenceTagsByOrderedCompetences,
} from "../tagUtils";

export type CompetenceTagsPickerFields = {
  competenceTagsIds: string[];
};

const CompetenceTagsPicker = () => {
  const {
    formState: { errors },
    register,
  } = useFormContext<CompetenceTagsPickerFields>();
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const hasSynced = useRef(false);

  const {
    data: enabledCompetences,
    isPending: isPendingCompetences,
    isError: isErrorCompetences,
    error: errorCompetences,
  } = useEnabledCompetences();

  const competenceTagGroupQueryOptions = tagsByGroupQueryOptions(
    tagGroupsSchema.enum.competence,
  );

  const {
    data: competenceTags,
    isPending: isPendingTags,
    isError: isErrorTags,
    error: errorTags,
  } = useQuery(competenceTagGroupQueryOptions);

  const { mutate: syncTags, isPending: isSyncing } = useMutation({
    mutationFn: async ({
      toCreate,
      toDelete,
    }: {
      toCreate: string[];
      toDelete: string[];
    }) => {
      await Promise.all([
        ...toCreate.map((displayName) => createTag(displayName)),
        ...toDelete.map((id) => deleteTag(id)),
      ]);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: competenceTagGroupQueryOptions.queryKey,
      });
    },
  });

  useEffect(() => {
    if (!enabledCompetences || !competenceTags || hasSynced.current) {
      return;
    }
    hasSynced.current = true;

    const { toCreate, toDelete } = computeCompetenceTagSync(
      enabledCompetences,
      competenceTags,
    );

    if (toCreate.length > 0 || toDelete.length > 0) {
      syncTags({ toCreate, toDelete });
    }
  }, [enabledCompetences, competenceTags, syncTags]);

  if (isPendingCompetences || isPendingTags || isSyncing) {
    return <Loading message={t`Hämtar lista med kompetenser`} />;
  }

  if (isErrorCompetences) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta lista med kompetenser.`} ${deducedError(errorCompetences)}`}
      />
    );
  }

  if (isErrorTags) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta kompetenstaggar.`} ${deducedError(errorTags)}`}
      />
    );
  }

  const sortedCompetenceTags =
    sortCompetenceTagsByOrderedCompetences(competenceTags);

  return (
    <Checkboxes
      legend={t`Kompetensflik`}
      description={t`Om ingen väljs finns mallen tillgänglig under samtliga kompetensflikar`}
      errorMessage={errors.competenceTagsIds?.message}
      orientation="horizontal"
    >
      {sortedCompetenceTags.map(({ id, displayName }) => {
        const parsed = medicalCompetenceSchema.safeParse(
          parseCompetenceTagName(displayName),
        );
        if (!parsed.success) {
          return (
            <Checkbox
              key={id}
              value={id}
              label={{ text: displayName }}
              visualStyle="framed"
              {...register("competenceTagsIds")}
            />
          );
        }
        return (
          <Checkbox
            key={id}
            value={id}
            label={{
              text: t(medicalCompetenceDictionary[parsed.data].short),
            }}
            visualStyle="framed"
            {...register("competenceTagsIds")}
          />
        );
      })}
    </Checkboxes>
  );
};

export { CompetenceTagsPicker };
